'use client'

import { useEffect, useState } from 'react'

import { readMediaObjectUrl } from '@/lib/api/client/inventory'
import type { YMarketItem } from '@/lib/types'

export interface ProductPrimaryPhoto {
  objectUrl: string | null
  loading: boolean
}

/**
 * The primary photo of the product an order line names, as a blob URL.
 *
 * Only a line `product_order_relation` has attached is read; `pictureUrl` is the
 * provider's offer picture and never stands in for it. The effect owns revoking.
 */
export function useProductPrimaryPhoto(item: YMarketItem | undefined): ProductPrimaryPhoto {
  const mediaUrl = item?.productId ? item.productPrimaryMediaUrl ?? null : null
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(Boolean(mediaUrl))

  useEffect(() => {
    const controller = new AbortController()
    let acquired: string | null = null
    setObjectUrl(null)
    setLoading(Boolean(mediaUrl))
    if (mediaUrl) {
      readMediaObjectUrl(mediaUrl, controller.signal)
        .then((url) => { acquired = url; setObjectUrl(url) })
        .catch(() => { if (!controller.signal.aborted) setObjectUrl(null) })
        .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    }
    return () => {
      controller.abort()
      if (acquired) URL.revokeObjectURL(acquired)
    }
  }, [mediaUrl])

  return { objectUrl, loading }
}

/**
 * The piece's photo as the top of an order card, sharp and fully visible.
 *
 * Decorative and inert: the card's own press target opens the order. A missing
 * photo keeps the same frame so cards in a row stay aligned.
 */
export function OrderCardPhoto({ objectUrl }: { objectUrl: string | null }) {
  return (
    <div
      data-testid={objectUrl ? 'order-card-photo' : 'order-card-photo-empty'}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-[inherit] bg-paper"
    >
      {objectUrl ? (
        // The frame stops short of the card bottom so the piece dips just under the frosted panel.
        // Studio shots sit on white; multiply lets that white take the card's warm ground.
        <img src={objectUrl} alt="" draggable={false} className="absolute inset-x-0 top-0 h-[calc(100%-2.5rem)] w-full object-contain p-3 mix-blend-multiply transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transition-none" />
      ) : (
        <svg className="absolute left-1/2 top-[38%] size-8 -translate-x-1/2 -translate-y-1/2 text-rule-strong" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="14" r="6" />
          <path d="m9.5 4.5 2.5 3 2.5-3" />
        </svg>
      )}
    </div>
  )
}
